import React, { useState } from 'react';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@roger/ui-components';
import { ChevronDown, Pencil, Trash2 } from 'lucide-react';
import { MarkdownContent } from '../MarkdownContent';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import {
  isProductUpdateExpired,
  productUpdateTargetLabels,
  type ProductUpdateManagementResponse,
} from '../../services/productUpdates';

export type ProductUpdateListRowProps = {
  row: ProductUpdateManagementResponse;
  permissionSlugToName: ReadonlyMap<string, string>;
  agencyIdToName: ReadonlyMap<string, string>;
  imageUrls: Record<string, string>;
  editorOpen: boolean;
  isDeleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

/**
 * Collapsed by default; markdown (and link previews) mount only while expanded.
 */
export const ProductUpdateListRow: React.FC<ProductUpdateListRowProps> = ({
  row,
  permissionSlugToName,
  agencyIdToName,
  imageUrls,
  editorOpen,
  isDeleting,
  onEdit,
  onDelete,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isExpired = isProductUpdateExpired(row.expires_at);
  const expiryPillLabel = row.expires_at
    ? `${isExpired ? 'Expired' : 'Expires'} ${formatDateToMMDDYYYY(row.expires_at)}`
    : 'Never expires';
  const targetLabels = productUpdateTargetLabels(
    row,
    permissionSlugToName,
    agencyIdToName
  );

  return (
    <li
      className={
        isExpired
          ? 'rounded-md border border-destructive bg-destructive/5 p-4'
          : 'rounded-md border p-4'
      }
    >
      <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
        <Collapsible
          open={expanded}
          onOpenChange={setExpanded}
          className='min-w-0 flex-1'
        >
          <CollapsibleTrigger className='group flex w-full items-center gap-2 text-left'>
            <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180' />
            <div className='flex min-w-0 flex-wrap items-center gap-2'>
              <span className='font-medium'>{row.version}</span>
              {targetLabels.map(label => (
                <Badge key={label} variant='secondary' className='font-normal'>
                  {label}
                </Badge>
              ))}
              <Badge
                variant={
                  isExpired
                    ? 'destructive'
                    : row.expires_at
                      ? 'outline'
                      : 'secondary'
                }
                className='font-normal'
              >
                {expiryPillLabel}
              </Badge>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className='pt-2 pl-6'>
            {expanded ? (
              <MarkdownContent content={row.markdown} imageUrls={imageUrls} />
            ) : null}
          </CollapsibleContent>
        </Collapsible>
        <div className='flex shrink-0 gap-2'>
          <Button
            size='sm'
            variant='outline'
            onClick={onEdit}
            disabled={editorOpen}
          >
            <Pencil className='h-4 w-4 mr-1' />
            Edit
          </Button>
          <Button
            size='sm'
            variant='outline'
            onClick={onDelete}
            disabled={isDeleting || editorOpen}
            aria-label={`Delete ${row.version}`}
          >
            <Trash2 className='h-4 w-4 text-destructive' />
          </Button>
        </div>
      </div>
    </li>
  );
};
